/**
 * `handoff` — the ONE place the FinderlyFix app handoff is edited.
 *
 * The widget decides the app button from two stored fields (see
 * `useAppHandoff` in common.tsx): `handoff.app_enabled` (the master switch)
 * and `copy.cta_app` (the label; `null` = only for out-of-scope problems).
 * Both are edited here. Scope, After diagnosis and Copy show the state
 * read-only with a link back, so no screen offers a control another one
 * silently overrides.
 *
 * `handoff.return_url` is stored but nothing reads it (not the widget, not
 * the app handoff), so it is not shown; a stored value is kept as it is.
 */
import { DEFAULT_APP_CTA_LABEL } from '../../../../diagnosis-widget/src/defaultCopy';
import { Field, Switch } from '../../ui/kit';
import { useDraft } from '../draft';
import { SectionCard, useAppHandoff, usePath } from './common';

export function HandoffSection() {
  const draft = useDraft();
  const app = useAppHandoff();
  const label = usePath<string | null>('copy.cta_app');
  return (
    <SectionCard id="sec-handoff" title="App handoff" description="Whether visitors can continue in the FinderlyFix app after a diagnosis.">
      <Switch
        label="Offer “Continue in the FinderlyFix app”"
        description="A button that opens the full DIY guide in the FinderlyFix app. You don’t receive a lead when a visitor takes it. Off removes the button everywhere in the widget."
        checked={app.enabled}
        onChange={(on) => {
          draft.update('handoff.app_enabled', on);
          if (on && label.value === null) label.set(DEFAULT_APP_CTA_LABEL);
        }}
      />
      {app.where === 'every' ? (
        <Field label="Button label" issues={label.issues}>
          {(p) => <input {...p} className="input" maxLength={120} value={label.value ?? ''} onChange={(e) => label.set(e.target.value)} />}
        </Field>
      ) : null}
      {app.where === 'out_of_scope' ? (
        <div className="notice info">
          <span>
            Right now the button shows only when a problem is outside your trades.{' '}
            <button type="button" className="btn-link" onClick={() => label.set(DEFAULT_APP_CTA_LABEL)}>
              Show it after every diagnosis
            </button>
          </span>
        </div>
      ) : null}
      <p className="small muted">
        Scope (problems outside your trades) and After diagnosis (the onward action) both follow this switch.
      </p>
    </SectionCard>
  );
}
